import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  AuthenticationFormData,
  AuthenticationOptions,
  AppleSignInFlow,
  AppleSignInOption
} from '../types/auth';
import { validateEmail, validatePassword } from '../utils/validation';
import { GoogleSignInButton } from './GoogleSignInButton';
import { AppleSignInButton } from './AppleSignInButton';
import { GoogleAuthErrorHandler } from './GoogleAuthErrorHandler';
import './LoginForm.css';

interface LoginFormProps {
  returnUrl?: string;
  onSuccess?: (token: string, redirectUrl: string) => void;
  clientId?: string;
  isEmailEnabled?: boolean;
  isGoogleEnabled?: boolean;
  isAppleEnabled?: boolean;
  /**
   * Apple sign-in paths this client offers, one button each. Supplied when the client's users
   * are split across Apple Developer teams; omit for the usual single-button case.
   */
  appleSignInOptions?: AppleSignInOption[];
}

/**
 * Identifies one Apple button among the options a client offers. Server-side options carry a key
 * naming their Apple Developer team; the Firebase option has none, and there is only ever one.
 */
const appleOptionId = (option: AppleSignInOption): string => option.key || option.flow;

/**
 * Login Form Component
 * Provides email/password authentication with Firebase integration
 */
export const LoginForm: React.FC<LoginFormProps> = ({ 
  returnUrl, 
  onSuccess, 
  clientId,
  isEmailEnabled = true,
  isGoogleEnabled = true,
  isAppleEnabled = true,
  appleSignInOptions
}) => {
  const { t } = useTranslation();
  const { authState, signIn, signInWithGoogle, signInWithApple, clearError } = useAuth();
  const [formData, setFormData] = useState<AuthenticationFormData>({
    email: '',
    password: '',
    returnUrl,
    clientId
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [authOptions, setAuthOptions] = useState<AuthenticationOptions>({
    showEmailForm: false, // Hidden by default - user clicks button to show
    showGoogleButton: isGoogleEnabled,
    isGoogleAuthInProgress: false,
    isEmailAuthInProgress: false,
    isRedirectInProgress: false,
    selectedAuthMethod: null
  });
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [appleError, setAppleError] = useState<string | null>(null);
  const [isAppleAuthInProgress, setIsAppleAuthInProgress] = useState(false);
  // Tracks which Apple button was pressed, so only that one shows a spinner. Identified by the
  // option's key rather than its flow, since a client can offer a server-side path per Apple
  // Developer team and 'server' alone would spin every one of them.
  const [activeAppleFlow, setActiveAppleFlow] = useState<string | null>(null);
  // The last Apple button pressed, so a retry goes back to the same Apple Developer team
  const [lastAppleAttempt, setLastAppleAttempt] = useState<{ flow: AppleSignInFlow; flowKey?: string } | null>(null);
  
  // Show Apple button if enabled in config - availability check happens during sign-in
  // This allows the button to be visible even if Apple SDK hasn't loaded yet
  // Clients whose users span two Apple Developer teams supply one option per team; everything
  // else falls back to a single unlabelled Firebase-popup button.
  const appleOptions: AppleSignInOption[] = appleSignInOptions?.length
    ? appleSignInOptions
    : isAppleEnabled
      ? [{ flow: 'firebase', label: '' }]
      : [];

  // Update return URL and client ID when props change
  useEffect(() => {
    setFormData(prev => ({ ...prev, returnUrl, clientId }));
  }, [returnUrl, clientId]);

  // Clear errors when form data changes
  useEffect(() => {
    if (authState.error) {
      clearError();
    }
  }, [formData.email, formData.password, clearError, authState.error]);

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = t(emailValidation.errorKey ?? 'validation.fallback.invalidEmail', emailValidation.params);
    }

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = t(passwordValidation.errorKey ?? 'validation.fallback.passwordRequired', passwordValidation.params);
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle input field changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Handle email/password form submission
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setAuthOptions(prev => ({
      ...prev,
      isEmailAuthInProgress: true,
      selectedAuthMethod: 'email',
      showGoogleButton: false
    }));

    try {
      await signIn(formData.email, formData.password, formData.returnUrl);
      
      // Check if we have platform response with token and redirect URL
      if (authState.platformResponse && onSuccess) {
        onSuccess(
          authState.platformResponse.platformToken,
          authState.platformResponse.redirectUrl
        );
      }
    } catch (error) {
      // Error handling is managed by the useAuth hook
      console.error('Login error:', error);
    } finally {
      setAuthOptions(prev => ({
        ...prev,
        isEmailAuthInProgress: false,
        selectedAuthMethod: null,
        showGoogleButton: true
      }));
    }
  };

  /**
   * Handle Google sign-in
   */
  const handleGoogleSignIn = async () => {
    setGoogleError(null);
    setAuthOptions(prev => ({
      ...prev,
      isGoogleAuthInProgress: true,
      selectedAuthMethod: 'google',
      showEmailForm: false
    }));

    try {
      console.log('Initiating Google sign-in from LoginForm');
      await signInWithGoogle(formData.returnUrl);
      
      // Check if we have platform response with token and redirect URL
      if (authState.platformResponse && onSuccess) {
        console.log('Google authentication successful, calling onSuccess callback');
        onSuccess(
          authState.platformResponse.platformToken,
          authState.platformResponse.redirectUrl
        );
      }
    } catch (error) {
      // Error handling is managed by the useAuth hook
      console.error('Google sign-in error in LoginForm:', error);
      
      // Set Google-specific error for enhanced error handling
      if (authState.error) {
        setGoogleError(authState.error);
      }
    } finally {
      setAuthOptions(prev => ({
        ...prev,
        isGoogleAuthInProgress: false,
        selectedAuthMethod: null,
        showEmailForm: true
      }));
    }
  };

  /**
   * Handle Google authentication retry
   */
  const handleGoogleRetry = () => {
    setGoogleError(null);
    handleGoogleSignIn();
  };

  /**
   * Handle Apple sign-in
   */
  const handleAppleSignIn = async (flow: AppleSignInFlow = 'firebase', flowKey?: string) => {
    setAppleError(null);
    setIsAppleAuthInProgress(true);
    setActiveAppleFlow(flowKey ?? flow);
    setLastAppleAttempt({ flow, flowKey });
    setAuthOptions(prev => ({
      ...prev,
      selectedAuthMethod: 'apple',
      showEmailForm: false,
      showGoogleButton: false
    }));

    try {
      console.log('Initiating Apple sign-in from LoginForm', { flow, flowKey });
      await signInWithApple(formData.returnUrl, flow, flowKey);
      
      // Check if we have platform response with token and redirect URL
      if (authState.platformResponse && onSuccess) {
        console.log('Apple authentication successful, calling onSuccess callback');
        onSuccess(
          authState.platformResponse.platformToken,
          authState.platformResponse.redirectUrl
        );
      }
    } catch (error) {
      // Error handling is managed by the useAuth hook
      console.error('Apple sign-in error in LoginForm:', error);
      
      // Set Apple-specific error for enhanced error handling
      if (authState.error) {
        setAppleError(authState.error);
      }
    } finally {
      setIsAppleAuthInProgress(false);
      setActiveAppleFlow(null);
      setAuthOptions(prev => ({
        ...prev,
        selectedAuthMethod: null,
        showEmailForm: true,
        showGoogleButton: true
      }));
    }
  };

  /**
   * Handle Apple authentication retry
   */
  const handleAppleRetry = () => {
    setAppleError(null);

    // Retry the button the user actually pressed. Defaulting to the Firebase path would quietly
    // move them to a different Apple Developer team, and Apple would hand back a different user
    // identifier - so a retry could land them in a new empty account instead of their own.
    if (lastAppleAttempt) {
      handleAppleSignIn(lastAppleAttempt.flow, lastAppleAttempt.flowKey);
    }
  };

  /**
   * Handle fallback to redirect flow for popup blocked scenarios
   */
  const handleFallbackToRedirect = async () => {
    setGoogleError(null);
    setAuthOptions(prev => ({
      ...prev,
      isRedirectInProgress: true,
      selectedAuthMethod: 'google'
    }));

    try {
      // This will trigger redirect flow which doesn't return immediately
      await signInWithGoogle(formData.returnUrl);
    } catch (error) {
      console.error('Google redirect fallback error:', error);
      if (authState.error) {
        setGoogleError(authState.error);
      }
      setAuthOptions(prev => ({
        ...prev,
        isRedirectInProgress: false,
        selectedAuthMethod: null
      }));
    }
  };

  /**
   * Dismiss Google error
   */
  const handleDismissGoogleError = () => {
    setGoogleError(null);
    clearError();
  };

  /**
   * Dismiss Apple error
   */
  const handleDismissAppleError = () => {
    setAppleError(null);
    clearError();
  };

  /**
   * Handle password reset link click
   */
  const handlePasswordResetClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // This will be handled by the parent component or router
    window.location.href = `/reset-password${returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''}`;
  };

  return (
    <div className="login-form-container">
      <div className="login-form">
        {/* Google-specific error handling */}
        {googleError && (
          <GoogleAuthErrorHandler
            error={googleError}
            onRetry={handleGoogleRetry}
            onFallbackToRedirect={handleFallbackToRedirect}
            onDismiss={handleDismissGoogleError}
            showFallbackOption={true}
          />
        )}

        {/* Apple-specific error handling */}
        {appleError && (
          <div className="login-form__error login-form__error--apple" role="alert" aria-live="polite">
            <p>{t(appleError)}</p>
            <div className="login-form__error-actions">
              <button
                type="button"
                onClick={handleAppleRetry}
                className="login-form__error-button login-form__error-button--retry"
              >
                {t('login.errorRetry')}
              </button>
              <button
                type="button"
                onClick={handleDismissAppleError}
                className="login-form__error-button login-form__error-button--dismiss"
              >
                {t('login.errorDismiss')}
              </button>
            </div>
          </div>
        )}

        {/* General authentication error (for email auth) */}
        {authState.error && !googleError && !appleError && (
          <div className="login-form__error" role="alert" aria-live="polite">
            {t(authState.error)}
          </div>
        )}

        {/* Social Sign-In Section */}
        <div className={`login-form__social-section ${authOptions.isEmailAuthInProgress ? 'login-form__social-section--hidden' : ''}`}>
          {/* Google Sign-In Button */}
          {authOptions.showGoogleButton && (
            <div className="login-form__google-section">
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                disabled={authState.loading || authOptions.isGoogleAuthInProgress || authOptions.isEmailAuthInProgress || isAppleAuthInProgress}
                loading={authOptions.isGoogleAuthInProgress}
                className="login-form__google-button"
              />
            </div>
          )}

          {/* Apple Sign-In Button(s) - one per Apple Developer team the client serves */}
          {appleOptions.length > 0 && (
            <div className="login-form__apple-section">
              {appleOptions.length > 1 && (
                <p className="login-form__apple-hint">
                  {t('apple.chooseAppHint')}
                </p>
              )}
              {appleOptions.map(option => (
                <AppleSignInButton
                  key={appleOptionId(option)}
                  onClick={() => handleAppleSignIn(option.flow, option.key)}
                  disabled={authState.loading || isAppleAuthInProgress || authOptions.isGoogleAuthInProgress || authOptions.isEmailAuthInProgress}
                  loading={isAppleAuthInProgress && activeAppleFlow === appleOptionId(option)}
                  label={option.label}
                  regions={option.regions}
                  className="login-form__apple-button"
                />
              ))}
            </div>
          )}
        </div>

        {/* Progress indicator for social authentication */}
        {isAppleAuthInProgress && (
          <div className="login-form__progress-indicator login-form__progress-indicator--apple">
            {t('login.progress.apple')}
          </div>
        )}

        {authOptions.isGoogleAuthInProgress && (
          <div className="login-form__progress-indicator login-form__progress-indicator--google">
            {authOptions.isRedirectInProgress ? t('login.progress.googleRedirect') : t('login.progress.google')}
          </div>
        )}

        {/* Email sign-in toggle button - show when email form is hidden but email is enabled */}
        {!authOptions.showEmailForm && isEmailEnabled && (
          <>
            {/* Divider if there are social buttons */}
            {(authOptions.showGoogleButton || appleOptions.length > 0) && (
              <div className="login-form__divider">
                <span className="login-form__divider-text">{t('login.divider')}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setAuthOptions(prev => ({ ...prev, showEmailForm: true }))}
              className="login-form__email-toggle-button"
              disabled={authState.loading || authOptions.isGoogleAuthInProgress || isAppleAuthInProgress}
            >
              {t('login.signInWithEmail')}
            </button>
          </>
        )}

        {/* Need help link - show when email is NOT enabled (no-email clients) */}
        {!isEmailEnabled && !authOptions.showEmailForm && (
          <div className="login-form__help-section">
            <a
              href="https://support.wehuntapp.com"
              className="login-form__help-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('login.needHelp')}
            </a>
          </div>
        )}

        {/* Divider between authentication methods - show when email form is visible */}
        {authOptions.showEmailForm && (authOptions.showGoogleButton || appleOptions.length > 0) && (
          <div className="login-form__divider">
            <span className="login-form__divider-text">{t('login.divider')}</span>
          </div>
        )}

        {/* Progress indicator for email authentication */}
        {authOptions.isEmailAuthInProgress && (
          <div className="login-form__progress-indicator login-form__progress-indicator--email">
            {t('login.progress.email')}
          </div>
        )}

        {/* Email/Password Form Section */}
        {authOptions.showEmailForm && (
          <form className={`login-form__email-form ${authOptions.isGoogleAuthInProgress || isAppleAuthInProgress ? 'login-form__email-form--hidden' : ''}`} onSubmit={handleSubmit} noValidate>
            {/* Close/Cancel button to hide email form */}
            <button
              type="button"
              onClick={() => {
                setAuthOptions(prev => ({ ...prev, showEmailForm: false }));
                setValidationErrors({});
                setFormData(prev => ({ ...prev, email: '', password: '' }));
              }}
              className="login-form__email-close-button"
              aria-label={t('login.closeEmailForm')}
            >
              ×
            </button>

            <div className="login-form__field">
              <label htmlFor="email" className="login-form__label">
                {t('login.emailLabel')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`login-form__input ${validationErrors.email ? 'login-form__input--error' : ''}`}
                placeholder={t('login.emailPlaceholder')}
                required
                autoComplete="email"
                aria-describedby={validationErrors.email ? 'email-error' : undefined}
                disabled={authOptions.isEmailAuthInProgress || authState.loading}
              />
              {validationErrors.email && (
                <div id="email-error" className="login-form__field-error" role="alert">
                  {validationErrors.email}
                </div>
              )}
            </div>

            <div className="login-form__field">
              <label htmlFor="password" className="login-form__label">
                {t('login.passwordLabel')}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`login-form__input ${validationErrors.password ? 'login-form__input--error' : ''}`}
                placeholder={t('login.passwordPlaceholder')}
                required
                autoComplete="current-password"
                aria-describedby={validationErrors.password ? 'password-error' : undefined}
                disabled={authOptions.isEmailAuthInProgress || authState.loading}
              />
              {validationErrors.password && (
                <div id="password-error" className="login-form__field-error" role="alert">
                  {validationErrors.password}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="login-form__submit"
              disabled={authOptions.isEmailAuthInProgress || authState.loading}
              aria-describedby="submit-status"
            >
              {authOptions.isEmailAuthInProgress || authState.loading ? t('login.submitting') : t('login.submit')}
            </button>

            {isEmailEnabled && (
              <div className="login-form__links">
                <a
                  href="#"
                  onClick={handlePasswordResetClick}
                  className="login-form__link"
                >
                  {t('login.forgotPassword')}
                </a>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};